import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Trash2, Edit, Search, Copy, Check, Wallet } from 'lucide-react';
import { adminApi } from '../services/adminApi';
import { Modal } from '../components/Modal';
import { Pagination } from '../components/Pagination';
import { COLORS } from '../../theme/colors';

const LIMIT = 30;

const SOURCE_OPTIONS = [
  { value: '', label: '-- None --' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'telegram', label: 'Telegram' },
  { value: 'other', label: 'Other' },
];

const SOURCE_BADGE_CLASSES = {
  linkedin: 'admin-badge admin-badge-info',
  telegram: 'admin-badge admin-badge-info',
  other: 'admin-badge admin-badge-warning',
};

const fmtK = (n) => (n >= 1000 ? (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K' : n);

function CopyButton({ text, label }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <button
      onClick={handleCopy}
      style={{
        background: 'none', border: 'none', cursor: 'pointer', padding: 4,
        color: copied ? 'var(--success)' : 'var(--text-muted)',
      }}
      title={`Copy ${label}`}
    >
      {copied ? <Check size={16} /> : <Copy size={16} />}
    </button>
  );
}

export function UsersPage() {
  const [searchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [modalState, setModalState] = useState('form'); // 'form' | 'success'
  const [createdCredentials, setCreatedCredentials] = useState(null);
  const [ledgerUser, setLedgerUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    workspace_id: '',
    workspace_name: '',
    is_superuser: false,
    source: '',
    profile_link: '',
  });

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.getUsers({
        search: search || undefined,
        skip: (page - 1) * LIMIT,
        limit: LIMIT
      });
      setUsers(data.items);
      setTotal(data.total);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  const loadWorkspaces = async () => {
    try {
      const data = await adminApi.getWorkspaces({ limit: 100 });
      setWorkspaces(data.items);
    } catch (error) {
      console.error('Failed to load workspaces:', error);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    loadWorkspaces();
  }, []);

  const deriveNameFromEmail = (email) => {
    if (!email || !email.includes('@')) return '';
    const prefix = email.split('@')[0];
    return prefix.replace(/[._-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const [nameManuallyEdited, setNameManuallyEdited] = useState(false);

  const handleEmailChange = (email) => {
    const updates = { ...formData, email };
    if (!editingUser && !nameManuallyEdited) {
      updates.name = deriveNameFromEmail(email);
    }
    setFormData(updates);
  };

  const handleNameChange = (name) => {
    setNameManuallyEdited(true);
    setFormData({ ...formData, name });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadUsers();
  };

  const getGeneralWorkspaceId = () => {
    const generalWs = workspaces.find(ws => ws.name === 'General');
    return generalWs ? generalWs.id : '';
  };

  const openCreateModal = () => {
    setEditingUser(null);
    setModalState('form');
    setCreatedCredentials(null);
    setNameManuallyEdited(false);
    setFormData({
      email: '',
      name: '',
      password: '',
      workspace_id: getGeneralWorkspaceId(),
      workspace_name: '',
      is_superuser: false,
      source: '',
      profile_link: '',
      });
    setModalOpen(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setModalState('form');
    setCreatedCredentials(null);
    setFormData({
      email: user.email,
      name: user.name,
      password: '',
      workspace_id: user.workspace_id,
      workspace_name: '',
      is_superuser: user.is_superuser,
      source: user.source || '',
      profile_link: user.profile_link || '',
    });
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const updateData = { name: formData.name, is_superuser: formData.is_superuser };
        if (formData.password) {
          updateData.password = formData.password;
        }
        if (formData.workspace_id !== editingUser.workspace_id) {
          updateData.workspace_id = formData.workspace_id;
        }
        await adminApi.updateUser(editingUser.id, updateData);
        setModalOpen(false);
      } else {
        const createData = {
          email: formData.email,
          is_superuser: formData.is_superuser,
        };
        if (formData.name) createData.name = formData.name;
        if (formData.password) createData.password = formData.password;
        if (formData.workspace_id) createData.workspace_id = formData.workspace_id;
        if (formData.workspace_name) createData.workspace_name = formData.workspace_name;
        if (formData.source) createData.source = formData.source;
        if (formData.profile_link) createData.profile_link = formData.profile_link;
        const result = await adminApi.createUser(createData);
        setCreatedCredentials({
          email: result.user.email,
          password: result.generated_password || formData.password,
        });
        setModalState('success');
      }
      loadUsers();
      loadWorkspaces();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save user');
    }
  };

  const handleCreateAnother = () => {
    setModalState('form');
    setCreatedCredentials(null);
    setNameManuallyEdited(false);
    setFormData({
      email: '',
      name: '',
      password: '',
      workspace_id: getGeneralWorkspaceId(),
      workspace_name: '',
      is_superuser: false,
      source: '',
      profile_link: '',
      });
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete user ${user.email}?`)) return;
    try {
      await adminApi.deleteUser(user.id);
      loadUsers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const totalPages = Math.ceil(total / LIMIT);


  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Users</h1>
        <button className="admin-btn admin-btn-primary" onClick={openCreateModal}>
          <Plus size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          Create User
        </button>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search size={18} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-disabled)' }} />
            <input
              type="text"
              className="admin-input"
              placeholder="Search by email or name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ paddingLeft: 40 }}
            />
          </div>
          <button type="submit" className="admin-btn admin-btn-secondary">Search</button>
        </div>
      </form>

      {/* Table */}
      <div className="admin-card" style={{ padding: 0 }}>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Source</th>
              <th>Chars</th>
              <th>Balance</th>
              <th>Workspace</th>
              <th>Verified</th>
              <th>Admin</th>
              <th>Created</th>
              <th style={{ width: 100 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={10} style={{ textAlign: 'center', padding: 40 }}>Loading...</td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={10} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No users found</td>
              </tr>
            ) : (
              users.map(user => (
                <tr key={user.id}>
                  <td>{user.email}</td>
                  <td>{user.name}</td>
                  <td>
                    {user.source && (
                      <span className={SOURCE_BADGE_CLASSES[user.source] || 'admin-badge'}>
                        {SOURCE_OPTIONS.find(o => o.value === user.source)?.label || user.source}
                      </span>
                    )}
                    {user.signup_attribution && (
                      <div
                        style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4, maxWidth: 260, overflowWrap: 'anywhere' }}
                        title={user.signup_attribution.referrer || ''}
                        data-testid="signup-attribution"
                      >
                        {user.signup_attribution.cta_path || user.signup_attribution.landing_path}
                        {user.signup_attribution.utm_source && ` · ${user.signup_attribution.utm_source}`}
                      </div>
                    )}
                  </td>
                  <td style={{ fontSize: 13 }}>
                    {(() => {
                      const fmt = (n) => n >= 1000 ? (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K' : n;
                      const lifetime = user.total_chars_used ?? 0;
                      // Metered users get a free_remaining breakdown from the API;
                      // unlimited (Admin Workspace) users don't.
                      const isLimited = user.total_remaining !== undefined;
                      if (!isLimited) {
                        return (
                          <>
                            <span style={{ color: 'var(--ink-700)' }}>{fmt(lifetime)}</span>
                            <span style={{ color: 'var(--text-disabled)' }}>{' / ∞'}</span>
                          </>
                        );
                      }
                      // Free allowance actually consumed THIS billing period, not the
                      // lifetime document sum - the latter against a monthly cap read
                      // as a limit bypass when it was just months of legitimate use.
                      const limit = user.free_limit ?? 0;
                      const used = Math.max(0, limit - (user.free_remaining ?? 0));
                      const exhausted = (user.free_remaining ?? 0) <= 0;
                      return (
                        <>
                          <span
                            style={{ color: exhausted ? 'var(--danger)' : 'var(--ink-700)' }}
                            title={`This period. Lifetime across all periods: ${fmt(lifetime)}`}
                          >
                            {fmt(used)}
                          </span>
                          <span style={{ color: 'var(--text-disabled)' }}>{' / '}{fmt(limit)}</span>
                        </>
                      );
                    })()}
                  </td>
                  <td style={{ fontSize: 13 }}>
                    {user.total_remaining === undefined ? (
                      <span style={{ color: 'var(--text-disabled)' }}>∞</span>
                    ) : (
                      <span title={`Free remaining ${user.free_remaining} + Purchased ${user.purchased_balance}`}>
                        <span style={{ color: 'var(--ink-700)' }}>{fmtK(user.free_remaining)}</span>
                        <span style={{ color: 'var(--text-disabled)' }}>{' + '}</span>
                        <span style={{ color: user.purchased_balance > 0 ? 'var(--success)' : 'var(--text-disabled)' }}>
                          {fmtK(user.purchased_balance)}
                        </span>
                      </span>
                    )}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{user.workspace_id}</td>
                  <td>
                    {user.is_verified === false ? (
                      <span className="admin-badge admin-badge-warning">No</span>
                    ) : (
                      <span className="admin-badge admin-badge-success">Yes</span>
                    )}
                  </td>
                  <td>
                    {user.is_superuser && (
                      <span className="admin-badge admin-badge-info">Admin</span>
                    )}
                  </td>
                  <td>{new Date(user.created_at).toLocaleDateString()}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={() => setLedgerUser(user)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                        title="Balance & ledger"
                      >
                        <Wallet size={16} color={COLORS.textMuted} />
                      </button>
                      <button
                        onClick={() => openEditModal(user)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                        title="Edit"
                      >
                        <Edit size={16} color={COLORS.textMuted} />
                      </button>
                      <button
                        onClick={() => handleDelete(user)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                        title="Delete"
                      >
                        <Trash2 size={16} color={COLORS.danger} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />

      {/* Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalState === 'success' ? 'User Created' : (editingUser ? 'Edit User' : 'Create User')}
      >
        {modalState === 'success' && createdCredentials ? (
          /* Success state - show credentials */
          <div>
            <div style={{
              background: 'var(--success-bg)', border: `1px solid ${COLORS.successBorder}`, borderRadius: 8,
              padding: 16, marginBottom: 20, textAlign: 'center',
            }}>
              <div style={{ color: 'var(--success)', fontWeight: 600, fontSize: 16 }}>
                User created successfully!
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label className="admin-label">Email</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="text"
                  className="admin-input"
                  value={createdCredentials.email}
                  readOnly
                  style={{ background: 'var(--bg)' }}
                />
                <CopyButton text={createdCredentials.email} label="email" />
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label className="admin-label">Password</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="text"
                  className="admin-input"
                  value={createdCredentials.password}
                  readOnly
                  style={{ background: 'var(--bg)', fontFamily: 'monospace' }}
                />
                <CopyButton text={createdCredentials.password} label="password" />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
              <CopyCredentialsButton credentials={createdCredentials} />
              <CopyLinkedInMessageButton credentials={createdCredentials} />
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="admin-btn admin-btn-secondary"
                onClick={() => setModalOpen(false)}
              >
                Close
              </button>
              <button
                type="button"
                className="admin-btn admin-btn-primary"
                onClick={handleCreateAnother}
              >
                Create Another
              </button>
            </div>
          </div>
        ) : (
          /* Form state */
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 16 }}>
              <label className="admin-label">Email</label>
              <input
                type="email"
                className="admin-input"
                value={formData.email}
                onChange={(e) => handleEmailChange(e.target.value)}
                required
                disabled={!!editingUser}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label className="admin-label">Name {!editingUser && <span style={{ color: 'var(--text-disabled)', fontWeight: 400 }}>(auto from email)</span>}</label>
              <input
                type="text"
                className="admin-input"
                value={formData.name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder={!editingUser ? 'Auto-derived from email' : ''}
                required={!!editingUser}
              />
            </div>

            {editingUser && (
              <div style={{ marginBottom: 16 }}>
                <label className="admin-label">Password (leave empty to keep current)</label>
                <input
                  type="password"
                  className="admin-input"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                />
              </div>
            )}

            {editingUser && (
              <div style={{ marginBottom: 16 }}>
                <label className="admin-label">Workspace</label>
                <select
                  className="admin-input"
                  value={formData.workspace_id}
                  onChange={(e) => setFormData({ ...formData, workspace_id: e.target.value })}
                  data-testid="user-workspace"
                >
                  {workspaces.map(ws => (
                    <option key={ws.id} value={ws.id}>{ws.name}</option>
                  ))}
                </select>
                {formData.workspace_id !== editingUser.workspace_id && (
                  <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-muted)' }}>
                    New documents are billed under this workspace's terms; documents already made stay as they were.
                  </div>
                )}
              </div>
            )}

            {editingUser && (
              <div style={{ marginBottom: 16, fontSize: 13, color: 'var(--text-muted)', background: 'var(--bg)', padding: '10px 12px', borderRadius: 6 }}>
                Plan (limited / unlimited) and postpaid terms are set on the user's workspace. Balance is adjusted via the ledger (View ledger → add credit).
              </div>
            )}

            <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
              <div style={{ flex: 1 }}>
                <label className="admin-label">Source</label>
                <select
                  className="admin-input"
                  value={formData.source}
                  onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                >
                  {SOURCE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label className="admin-label">Profile link</label>
                <input
                  type="text"
                  className="admin-input"
                  value={formData.profile_link}
                  onChange={(e) => setFormData({ ...formData, profile_link: e.target.value })}
                  placeholder="URL or username"
                />
              </div>
            </div>

            {!editingUser && (
              <>
                <div style={{ marginBottom: 16 }}>
                  <label className="admin-label">Workspace</label>
                  <select
                    className="admin-input"
                    value={formData.workspace_id}
                    onChange={(e) => setFormData({ ...formData, workspace_id: e.target.value })}
                  >
                    <option value="">-- Create new workspace --</option>
                    {workspaces.map(ws => (
                      <option key={ws.id} value={ws.id}>{ws.name}</option>
                    ))}
                  </select>
                </div>

                {!formData.workspace_id && (
                  <div style={{ marginBottom: 16 }}>
                    <label className="admin-label">New Workspace Name (optional)</label>
                    <input
                      type="text"
                      className="admin-input"
                      value={formData.workspace_name}
                      onChange={(e) => setFormData({ ...formData, workspace_name: e.target.value })}
                      placeholder="Leave empty for auto-generated name"
                    />
                  </div>
                )}
              </>
            )}

            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.is_superuser}
                  onChange={(e) => setFormData({ ...formData, is_superuser: e.target.checked })}
                />
                Administrator
              </label>
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="admin-btn admin-btn-secondary" onClick={() => setModalOpen(false)}>
                Cancel
              </button>
              <button type="submit" className="admin-btn admin-btn-primary">
                {editingUser ? 'Save' : 'Create'}
              </button>
            </div>
          </form>
        )}
      </Modal>

      <LedgerModal
        user={ledgerUser}
        onClose={() => { setLedgerUser(null); loadUsers(); }}
      />
    </div>
  );
}

const TX_TYPE_LABELS = {
  debit_translation: 'Debit',
  refund: 'Refund',
  payg_purchase: 'Purchase',
  admin_adjust: 'Admin credit',
};

function LedgerModal({ user, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creditChars, setCreditChars] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      setData(await adminApi.getUserLedger(user.id, { limit: 100 }));
    } catch (e) {
      console.error('Failed to load ledger:', e);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  if (!user) return null;

  const handleCredit = async (e) => {
    e.preventDefault();
    const chars = parseInt(creditChars, 10);
    if (!chars || chars <= 0) return;
    setBusy(true);
    try {
      // Unique key per submit so a network retry / double-submit can't double-credit.
      const idempotency_key = `admin-${user.id}-${crypto.randomUUID()}`;
      await adminApi.creditUser(user.id, { chars, reason: creditReason, idempotency_key });
      setCreditChars('');
      setCreditReason('');
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to credit user');
    } finally {
      setBusy(false);
    }
  };

  const handleRefund = async (txId) => {
    if (!window.confirm('Refund this charge back to the user?')) return;
    setBusy(true);
    try {
      await adminApi.refundTransaction(txId, 'admin refund');
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to refund');
    } finally {
      setBusy(false);
    }
  };

  const bal = data?.balance;
  const reconcile = data?.reconcile;

  return (
    <Modal isOpen={!!user} onClose={onClose} title={`Balance & ledger - ${user.email}`}>
      {loading && !data ? (
        <div style={{ padding: 24, textAlign: 'center' }}>Loading...</div>
      ) : (
        <div>
          {/* Balance summary */}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
            {bal ? (
              <>
                <Stat label="Free remaining" value={`${bal.free_remaining} / ${bal.free_limit}`} />
                <Stat label="Purchased" value={bal.purchased_balance} accent={bal.purchased_balance > 0} />
                <Stat label="Total available" value={bal.total_remaining} />
                <Stat label="Days to reset" value={bal.days_until_reset ?? '-'} />
              </>
            ) : (
              <Stat label="Plan" value="Unlimited" />
            )}
          </div>

          {reconcile && !reconcile.ok && (
            <div className="admin-badge admin-badge-warning" style={{ marginBottom: 16 }}>
              ⚠ Ledger mismatch: purchased counter {reconcile.actual} ≠ ledger {reconcile.expected}
            </div>
          )}

          {/* Add credit */}
          <form onSubmit={handleCredit} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 20, flexWrap: 'wrap' }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Add credit (chars)</label>
              <input className="admin-input" type="number" min="1" value={creditChars}
                     onChange={(e) => setCreditChars(e.target.value)} placeholder="50000" style={{ width: 140 }} />
            </div>
            <div style={{ flex: 1, minWidth: 160 }}>
              <label style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Reason</label>
              <input className="admin-input" type="text" value={creditReason}
                     onChange={(e) => setCreditReason(e.target.value)} placeholder="e.g. paid via Telegram" />
            </div>
            <button type="submit" className="admin-btn admin-btn-primary" disabled={busy}>Add credit</button>
          </form>

          {/* Ledger table */}
          <div style={{ maxHeight: 360, overflowY: 'auto' }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Date</th><th>Type</th><th>Chars</th><th>Reason</th><th>Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {(data?.items || []).length === 0 ? (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)' }}>No transactions</td></tr>
                ) : (
                  data.items.map((tx) => (
                    <tr key={tx.id}>
                      <td style={{ fontSize: 12 }}>{new Date(tx.created_at).toLocaleString()}</td>
                      <td>{TX_TYPE_LABELS[tx.type] || tx.type}</td>
                      <td style={{ color: tx.chars_delta < 0 ? 'var(--danger)' : 'var(--success)' }}>
                        {tx.chars_delta > 0 ? '+' : ''}{tx.chars_delta}
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{tx.reason}</td>
                      <td>
                        {tx.status === 'refunded'
                          ? <span className="admin-badge admin-badge-warning">refunded</span>
                          : <span className="admin-badge">{tx.status}</span>}
                      </td>
                      <td>
                        {tx.type === 'debit_translation' && tx.status === 'active' && (
                          <button className="admin-btn admin-btn-secondary" disabled={busy}
                                  onClick={() => handleRefund(tx.id)} style={{ padding: '2px 10px', fontSize: 12 }}>
                            Refund
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Modal>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div style={{ minWidth: 110 }}>
      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: accent ? 'var(--success)' : 'var(--text)' }}>{value}</div>
    </div>
  );
}

function CopyCredentialsButton({ credentials }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const text = `Email: ${credentials.email}\nPassword: ${credentials.password}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <button
      type="button"
      className={`admin-btn ${copied ? 'admin-btn-secondary' : 'admin-btn-primary'}`}
      onClick={handleCopy}
      style={{ width: '100%' }}
    >
      {copied ? (
        <><Check size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />Copied!</>
      ) : (
        <><Copy size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />Copy Credentials</>
      )}
    </button>
  );
}

function CopyLinkedInMessageButton({ credentials }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const text = `Here's your access:\n\nhttps://translator.chatscontrol.com\n\nEmail: ${credentials.email}\nPassword: ${credentials.password}\n\nUpload any document, check the result. Let me know your thoughts - quality, usability, what's missing. No rush, whenever you have time`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <button
      type="button"
      className={`admin-btn ${copied ? 'admin-btn-secondary' : 'admin-btn-primary'}`}
      onClick={handleCopy}
      style={{ width: '100%' }}
    >
      {copied ? (
        <><Check size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />Copied!</>
      ) : (
        <><Copy size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />Copy LinkedIn Message</>
      )}
    </button>
  );
}
